export interface ApplicationMetadata {
  name: string;
  context?: string;
  data?: Record<string, string | number | boolean | (string | number | boolean)[]>;
}

export interface ConnectionParams {
  user_id: string;
  company_id: string;
  user_group_id?: string;
  spoof_user_id?: number;
  request_id: string;
  application_metadata?: ApplicationMetadata;
}

export type WindowFunction = 'sum' | 'avg' | 'min' | 'max' | 'last' | 'rate' | 'count' | 'delta' | 'none';
export type AggregateFunction = 'sum' | 'avg' | 'min' | 'max' | 'last' | 'count' | 'none';
export type MergeFunction = 'sum' | 'avg' | 'min' | 'max';
export type RollupFunction = 'sum' | 'avg' | 'min' | 'max' | 'last' | 'rate' | `p${number}` | 'composite';

export type Direction = 'src' | 'dst' | 'both';

export interface MeasurementDimension {
  Type?: string;
  Label?: string;
  Column?: string;
  Alias?: string;
  Direction?: 'src' | 'dst';
  // optional key of the dimension in the inverse direction
  Inverse?: string;
  /**
   * Optional column to use when filtering on this dimension.
   * Use when the display column differs from the filter column (e.g., i_src_as_name vs src_as).
   */
  FilterColumn?: string;
  /** NMS: the Data Explorer dimension key that maps to this kmetrics dimension */
  QueryColumn?: string;
  Values?: Record<string | number, string>;
  Unit?: string;
  Kind?: string;
  Mask?: number;
  Category?: string;
  Popular?: boolean;
  Shared?: boolean;
  /** When true, this dimension is only available for filtering, not group-by. */
  FilterOnly?: boolean;
  LastSeen?: string | null;
  Usage?: Record<string, boolean>;
}

export interface DirectionalMeasurementDimension extends Omit<MeasurementDimension, 'Column'> {
  Directions: Partial<
    Record<
      Direction,
      MeasurementDimension & {
        // This key should match what normally would have been the key name of the dimension in dictionary
        key: string;
      }
    >
  >;
}

export interface MeasurementMetric extends MeasurementDimension {
  Window?: WindowFunction;
  Aggregate?: AggregateFunction;
  Alias?: string;
  ToBits?: boolean;
  // expression that's used to select the data, instead of a plain column
  Expression?: string;
  // other metrics that this expression depends on
  DependsOn?: string[];
  // Whether to show rollup/aggregation selector in UI. Defaults to true.
  Rollup?: boolean;
  /**
   * For enum metrics (when Values is set), the value considered "healthy" for
   * visualization purposes (e.g. coloring the timeline view). Omit when no
   * health interpretation applies (descriptive enums like operating-mode).
   */
  HealthyValue?: number | string;
}

export type MeasurementItem<T extends 'metric' | 'dimension' = 'metric' | 'dimension'> = T extends 'metric'
  ? MeasurementMetric
  : MeasurementDimension;

export type MeasurementItemMap<T extends 'metric' | 'dimension'> = Record<string, MeasurementItem<T>>;

// NMS specific measurement tags are defined here:
// https://github.com/kentik/ranger-config/blob/293c3c00b38bd4f9ed051d9e1f4486ba3236cab1/docs/schema-guide.md#L54
export const measurementTagEnums = {
  CUSTOM_SNMP: 'custom_snmp',
  ESSENTIAL: 'essential',
  NMS: 'nms',
  KENTIK_TELEMETRY: 'kentik_telemetry'
} as const;

export type MeasurementTag = (typeof measurementTagEnums)[keyof typeof measurementTagEnums] | (string & {});

export interface Measurement {
  id?: number;
  measurement: string;
  displayName?: string;
  last_seen?: string | null;
  storage: {
    Metrics: { [name: string]: MeasurementMetric };
    Dimensions: { [name: string]: MeasurementDimension };
    SupportsFastData?: boolean;
    Description?: string;
    EntityType?: string;
    AppProtocol?: number;
    SourceTimestampCol?: string;
  };
  tags?: MeasurementTag[];
}

export type Dimension = {
  name: string;
  cidr?: number;
  cidr6?: number;
  cut?: { regex: string; selector: string };
  hostnameLookup?: boolean;
  useAsGroups?: boolean;
};
export type Metric = {
  name: string;
  type?: 'counter' | 'gauge' | 'string' | 'histogram' | 'expression';
};

export type Rollup = {
  // must match a name in query.metrics
  metric: string;
  fn: RollupFunction;
};

export type TimeRange = {
  /** Lookback in seconds. Takes precedence over `time.start` and `time.end` if not 0. */
  lookback: number;
  /** Starting timestamp in epoch seconds. Only used if `time.lookback` is 0. */
  start: number;
  /** Ending timestamp in epoch seconds. Only used if `time.lookback` is 0 */
  end: number;
  /** Start and end adjustment in seconds */
  adjust?: number;
  /** 'UTC', 'Local', or an IANA timezone string like 'America/New_York' */
  timezone?: string;
};

export type VizType =
  | 'stackedArea'
  | 'stackedAreaHundredPercent'
  | 'line'
  | 'stackedBar'
  | 'horizon'
  | 'bar'
  | 'pie'
  | 'sankey'
  | 'sunburst'
  | 'matrix'
  | 'gauge'
  | 'table'
  | 'geoHeatMap'
  | 'timelineView';

export interface Filter {
  filterField: string;
  operator: string;
  filterValue: string;
  rightFilterField?: string;
  disabled?: boolean;
}

export interface MetricFilter extends Filter {
  metric?: string;
  aggregate?: RollupFunction | '';
}

// Dimension filters will just use the base Filter interface
export interface FilterGroup {
  connector: 'All' | 'Any';
  name?: string;
  named?: boolean;
  autoAdded?: string;
  saved_filters?: { filter_id: number; is_not?: boolean }[];
  not: boolean;
  disabled?: boolean;
  filters: Filter[];
  filterGroups?: FilterGroup[];
}

export interface Filters {
  connector: 'All' | 'Any';
  filterGroups?: FilterGroup[];
  not?: boolean;
}

export interface MetricFilterGroup extends FilterGroup {
  filters: MetricFilter[];
  filterGroups?: MetricFilterGroup[];
}

export interface MetricFilters extends Filters {
  filterGroups: Array<MetricFilterGroup>;
}

export type MeasurementPrefix = '/traffic' | '/metrics' | '/synthetics' | '/events';
export type MeasurementName = MeasurementPrefix | `${MeasurementPrefix}/${string}`;

export interface Query {
  measurement: MeasurementName;
  dimensions: Dimension[];
  metrics: Metric[];
  filters: {
    sources?: Filters;
    dimensions?: Filters;
    metrics?: MetricFilters;
  };
  time: TimeRange;
  window?: {
    /** Size of each window in seconds */
    size: number;
    /** Optional agg to apply to each metric */
    fn?: { [name: string]: WindowFunction };
  };
  reAgg?: {
    fn: MergeFunction;
    /** Dimensions to merge when re-aggregating, using the given function. If not specified, all dimensions not in `query.dimensions` are merged */
    dimensions?: string[];
  };
  rollups: Rollup[];
  sort?: { rollup: string; order?: 'asc' | 'desc' }[];
  viz?: { type: VizType; limit: number };
  limit: number;
  updateFrequency?: number;
  liveUpdate?: boolean;
  rawLegend?: { dimensions?: string[]; limit?: number };
}

export type QueryWithConnection = Query & { connectionParams: ConnectionParams };
export type QueryWithMeasurement = Query & { measurementObj: Measurement };
export type QueryWithConnectionAndMeasurement = QueryWithConnection & { measurementObj: Measurement };

export type SqlVariant = 'original' | 'fastbit';

export interface SqlQuery {
  queries: { query: string; args?: string[]; generator?: SqlVariant }[];
  connectionParams: ConnectionParams;
}

export interface UdeSqlChunkWindow {
  index: number;
  start: number;
  end: number;
  durationSeconds: number;
}

export interface UdeSqlChunk extends UdeSqlChunkWindow {
  sql: string[];
}

export interface UdeSqlChunking {
  chunked: boolean;
  chunkCount: number;
  chunkSizeSeconds: number | null;
  windowSizeSeconds: number;
  totalDurationSeconds: number;
  liveUpdate: boolean;
  windows: UdeSqlChunkWindow[];
}

export interface UdeSqlResult {
  queries: string[];
  statements: SqlQuery['queries'];
  chunking: UdeSqlChunking;
  chunks: UdeSqlChunk[];
}

export interface SqlRow {
  timestamps: string;
  interval: string;
  /** dimension values */
  [key: `d_${string}`]: string;
  /** metric values */
  [key: `m_${string}`]: string;
}

export interface QueryResultRow {
  /** unique ID for the row, usually a concatenation of the dimension values */
  id: string;
  /** enriched version of id with human-readable names substituted for raw IDs (e.g. device name instead of device ID) */
  enrichedId?: string;
  /** optional timestamps for the values, supersedes timestamps in `QueryResults` */
  timestamps?: number[];
  /** values for each metric; the values correspond to the timestamps in `QueryResults` */
  values: { [metric: string]: { values: (number | null)[] } };
  /** value for each rollup */
  rollups: { [rollup: string]: number };
  /** value for each dimension */
  dimensions: { [dimension: string]: string };
}

export interface RawEventRow {
  [dimensionKey: string]: string | number;
}

export interface QueryResults {
  /** timestamps in epoch seconds shared by all rows */
  timestamps: number[];
  rows: QueryResultRow[];
  /** true when this payload is an incremental live update (not a full result set) */
  liveUpdate?: boolean;
  /** the lookback window size in seconds, used by the client to trim old data */
  windowSize?: number;
  parentRequestID?: string;
  requestID?: string;
  rawRows?: RawEventRow[];
  rawColumnKeys?: string[];
}
